<template>
  <div class="one-pager-page" :class="{isPdf}" :style="{'--scale': scale}">
    <!-- outside the scaled sheet: buttons at their own size on every screen -->
    <nav v-if="!isPdf" class="web-bar" aria-label="One-pager">
      <!-- Back and the page's title, as on a poster's page (PosterPage.vue: its collection) -->
      <div class="bar-start">
        <a href="/" class="back" aria-label="Back" title="Back" @click.prevent="goBack">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="BACK_ICON" /></svg>
        </a>
        <span class="page-title">About</span>
      </div>
      <a class="download" :href="pdfUrl" download>Download PDF</a>
    </nav>
    <div class="frame">
      <article class="sheet" :data-ready="isReady || undefined">
        <!-- the page's own address, linked (Seán, 2026-11-05; was "vizbi.org > one-pager") -->
        <p v-if="isPdf" class="crumb">
          <a href="https://vizbi.org/about">vizbi.org/about</a>
        </p>
        <!-- the year's logo animation (2026/Index.vue); in the PDF, its still -->
        <div class="logo">
          <div class="logo-frame">
            <img v-if="isPdf" :src="LOGO_STILL" alt="VIZBI">
            <HeroVideoVimeo v-else :id="LOGO_VIMEO_ID" />
          </div>
        </div>
        <p class="kicker">An International Meeting Series on</p>
        <h1 class="title">Visualizing<br>Biological<br>Data</h1>
        <!-- a sentence a line on narrow screens; on one line, as the audiences below -->
        <p class="tagline">
          <span>Inspiring Life Science Breakthroughs.</span> <span>Cutting-Edge DataVis Advances.</span>
        </p>
        <p class="lead">
          Advances in visual analytics and communication tools have not kept pace with the growing
          complexity of life science data. Many scientists struggle to make sense of their data and
          to share their insights. Founded in 2010, VIZBI bridges this gap by fostering an open,
          global, and diverse community bringing together:
        </p>
        <!-- two pairs: on narrow screens, one per line (.pair) -->
        <p class="audiences">
          <!-- on one line: Vue drops the spaces between elements across a line break -->
          <span class="pair">Life Scientists <span>•</span> Computer Scientists</span> <span>•</span> <span class="pair">Visual Designers <span>•</span> Science Communicators</span>
        </p>
        <h2 class="heading format">Format</h2>
        <p class="body format">
          <span class="tight">Each meeting attracts {{ figures.participantsPerMeeting }} participants
            from research, industry, and visual arts. Sessions</span> cover visual analytics challenges with
          cutting-edge data on DNA, RNA, proteins, cells, tissues, and ecosystems. Keynotes cover visualization principles, visual
          design, and science communication. All participants can present a lightning talk and
          poster, and take part in breakout discussions on cutting-edge topics.
        </p>
        <h2 class="heading value">Value for Participants</h2>
        <p class="body value">
          For {{ figures.years }} years, participants have described the VIZBI experience as
          outstanding. They gain an overview of key advances across life sciences, as well as
          evolving practices in data visualization and science communication. Emerging scientists
          and creators get feedback and recognition. VIZBI also provides unique networking with a
          highly influential and interdisciplinary community of leading experts.
        </p>
        <h2 class="heading highlights">Highlights &amp; Impact</h2>
        <hr class="rule top">
        <a v-for="cover in COVERS" :key="cover.place" :class="['cover', cover.place]"
           :href="cover.href" :title="isPdf ? undefined : cover.hint">
          <img :src="`${IMAGES}/${cover.image}`" :alt="cover.label">
        </a>
        <dl class="figures">
          <div v-for="figure in figureCells" :key="figure.label" :class="['figure', figure.place]">
            <dt>{{ figure.label }}</dt>
            <dd>
              <a v-if="figure.path" :href="getSiteHref(figure.path)"
                 :title="isPdf ? undefined : figure.hint"
                 @click="openOnSite($event, figure.path)">{{ figure.value }}</a>
              <template v-else>{{ figure.value }}</template>
            </dd>
          </div>
        </dl>
        <img class="map" :src="`${IMAGES}/map.png`" alt="World map of VIZBI's website visitors">
        <hr class="rule bottom">
        <!-- the one section that differs between the versions (VERSIONS) -->
        <template v-if="version === 'ask'">
          <h2 class="heading closing">Ask</h2>
          <p class="body closing">
            We seek {{ figures.ask }} to deliver VIZBI&#8239;{{ figures.askYear }} and enhance our
            website, providing greater value to both meeting participants and the broader research
            community. VIZBI operates as a nonprofit, with finances managed by ISCB.
          </p>
        </template>
        <template v-else>
          <h2 class="heading closing">Sponsorship Opportunities</h2>
          <p class="body closing">
            For recruitment or marketing, VIZBI provides access to a small but highly influential
            community of top visual analytics talent in the life sciences—and beyond. VIZBI
            operates as a nonprofit, with finances managed by ISCB.
          </p>
        </template>
        <h2 class="heading leadership">Leadership</h2>
        <div class="body leadership">
          <p>
            VIZBI is led by Prof.&#8239;Seán O'Donoghue (UNSW, AU), Dr&#8239;Jim Procter
            (U. Dundee, UK), &amp; Prof.&#8239;Barbora&#8239;Kozlíková (Masaryk U., CZ).
          </p>
          <p class="contact">
            <a href="mailto:contact@vizbi.org">contact@vizbi.org</a>
            <span aria-hidden="true">|</span>
            <a href="https://calendly.com/vizbi">calendly.com/vizbi</a>
          </p>
        </div>
      </article>
    </div>
    <!-- the version, for admins and editors: floating over the sheet, near the bottom -->
    <div v-if="!isPdf && isStaff" class="version-switch">
      <SegmentedControl v-model="versionLabel" :options="VERSION_LABELS" label="Version" />
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {getFullstackUrl} from "@/composables/useFullstackUrl";
import HeroVideoVimeo from "@/components/HeroVideoVimeo.vue";
import SegmentedControl from "@/components/App/SegmentedControl.vue";
import useRoles from "@/composables/useRoles";

const IMAGES = "/images/one-pager";
const BACK_ICON = "M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20z"; // PosterPage.vue's
// the versions (Seán, 2026-11-05), by the URL's ?version= (none: general, the default); they
// differ only in the closing section: Sponsorship Opportunities (general) or the specific Ask
const VERSIONS = {General: "general", Ask: "ask"};
const VERSION_LABELS = Object.keys(VERSIONS);
const LOGO_VIMEO_ID = "1145497607"; // 2026's hero animation (Leonora Martínez-Nuñez)
const LOGO_STILL = "/2026/Hero-trim.jpg"; // its frame, 16:9 like the video
const SHEET_WIDTH = 794; // A4 at 96dpi: the sheet's layout width, here and in the PDF
const MAX_WIDTH = 960;
const PAGE_MARGIN = 24; // px either side of the sheet on the web, as a PDF viewer shows a page
// each figure: its key in the figures, label, place, and the site's page for it (if any), with
// its hover text on the web (Seán, 2026-11-05)
const FIGURE_PLACES = [
  ["alumni", "Community members", "row1 col1"],
  ["posters", "Posters", "row1 col2", "/Posters", "Browse VIZBI's posters"],
  ["artworks", "Artworks", "row2 col1", "/art", "Browse VIZBI's artworks"],
  ["videos", "Videos", "row2 col2", "/Videos", "Browse VIZBI's videos"],
  ["visitorsPerYear", "Website visitors last year", "row2 col3"],
];
// the two features in the press, linked to the articles (Seán, 2026-11-05)
// Nature Methods: the supplement's collection page (the issue page, volumes/7/issues/3s, gives
// a server error, 2026-11-05)
const COVERS = [
  {place: "methods", image: "nature-methods.png",
    href: "https://www.nature.com/collections/iecaaechei",
    label: "Nature Methods supplement: Visualizing biological data (2010)",
    hint: "Nature Methods' special issue from VIZBI 2010: Visualizing Biological Data"},
  {place: "nature", image: "nature.png", href: "https://www.nature.com/articles/535187a",
    label: "Nature: The visualizations transforming biology (2016)",
    hint: "Nature's feature on VIZBI 2016: The visualizations transforming biology"},
];
const SITE = "https://vizbi.org";

// the page's query with this version (general: no ?version=, the default)
function getVersionQuery(versionName) {
  const query = {...route.query};
  delete query.version;
  return versionName === "general" ? query : {...query, version: versionName};
}

function getFigureText(value) {
  if (typeof value === "number") return value.toLocaleString("en-US");
  return value ?? "";
}

async function fetchFigures() {
  try {
    const response = await fetch(`${getFullstackUrl()}/api/v1/one-pager.json`);
    return response.ok ? await response.json() : {};
  } catch {
    return {};
  }
}

const route = useRoute();
const router = useRouter();
const isPdf = computed(() => route.query.pdf !== undefined);
const {isStaff} = useRoles();
const version = computed(() => (route.query.version === "ask" ? "ask" : "general"));
const versionLabel = computed({
  get: () => VERSION_LABELS.find((label) => VERSIONS[label] === version.value),
  set: (label) => router.replace({query: getVersionQuery(VERSIONS[label])}),
});
// the PDFs, made before each deploy (fullstack scripts/one-pager.mjs) and served as files
const pdfUrl = computed(() => {
  const file = version.value === "general" ? "one-pager.pdf" : `one-pager-${version.value}.pdf`;
  return `${getFullstackUrl()}/api/v1/${file}`;
});
const figures = ref({});
const isReady = ref(false);
const scale = ref(1);
const figureCells = computed(() => FIGURE_PLACES.map(([key, label, place, path, hint]) => (
  {label, place, path, hint, value: getFigureText(figures.value[key])})));

// the site's pages: in the PDF, vizbi.org's full address; on the web, this site's, in the app
function getSiteHref(path) {
  return isPdf.value ? `${SITE}${path}` : path;
}

function openOnSite(event, path) {
  if (isPdf.value) return;
  event.preventDefault();
  router.push(path);
}

// back where the visitor came from: within the app (the router's history), or from another of
// this site's pages loaded in full (e.g. an old link to /one-pager); opened directly, home
function goBack() {
  if (window.history.state?.back) return router.back();
  if (document.referrer.startsWith(window.location.origin)) return window.history.back();
  router.push("/");
}

// laid out once at A4's width, then scaled to the window: the same line breaks at every width
function updateScale() {
  const margins = isPdf.value ? 0 : 2 * PAGE_MARGIN;
  scale.value = Math.min(document.documentElement.clientWidth - margins, MAX_WIDTH) / SHEET_WIDTH;
}

// the PDFs' maker (fullstack scripts/one-pager.mjs) waits for data-ready: figures and fonts in
onMounted(async () => {
  updateScale();
  window.addEventListener("resize", updateScale);
  figures.value = await fetchFigures();
  await document.fonts.ready;
  isReady.value = true;
});
onBeforeUnmount(() => window.removeEventListener("resize", updateScale));
</script>

<style scoped>
/* The Canva one-pager (Seán, 2026-11-05): one A4 page, every box where the design has it, in
   --u = 10pt of the design (595.5 × 842.25pt; --x, --y, --width per box, --size its font). The
   sheet is laid out 794px wide (A4 at 96dpi, as the PDF renders it) and scaled to the window
   (--scale, updateScale), so it grows and shrinks as /Videos/Unlock does, text included, with
   the same line breaks at every width */
.one-pager-page {
  --u: calc(794px / 59.55);
  --green: var(--brand-color, #3c9);
  --blue: #38b6ff;
  background: black;
  color: #fefefe;
  min-height: 100vh;
  font-family: var(--font-family);
  /* Canva sets Nunito Sans at its 10pt optical size, a little looser than the web's; this
     spacing gives the design's line breaks */
  letter-spacing: 0.02em;
  font-weight: 400; /* the design's Regular, not the site's 500 (global.css) */
  text-align: left;
}
.frame {
  width: calc(59.55 * var(--u) * var(--scale));
  height: calc(84.2 * var(--u) * var(--scale));
  margin: 0 auto;
  overflow: hidden;
}
.sheet {
  position: relative;
  width: calc(59.55 * var(--u));
  height: calc(84.2 * var(--u));
  overflow: hidden;
  transform: scale(var(--scale));
  transform-origin: 0 0;
}
.sheet > * {
  --size: 1.199;
  position: absolute;
  left: calc(var(--x, 0) * var(--u));
  top: calc(var(--y, 0) * var(--u));
  width: var(--width, auto);
  margin: 0;
  font-size: calc(var(--size) * var(--u));
  line-height: 1.4;
}
.sheet a {
  color: var(--blue);
  font-weight: inherit;
  text-decoration: underline;
  text-underline-offset: 0.15em;
}
.crumb { --x: 2.83; --y: 1.6; --size: 0.999; }
/* the top bar, fixed: Back and Download PDF always at hand as the page scrolls (Seán,
   2026-11-05); as wide as the sheet, on black, the sheet starting below it */
.web-bar {
  --bar-height: 3.25rem;
  position: fixed;
  top: 0;
  left: 50%;
  z-index: 20;
  transform: translateX(-50%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  width: calc(59.55 * var(--u) * var(--scale));
  height: var(--bar-height);
  padding: 0 calc(2.83 * var(--u) * var(--scale));
  background: black;
  font-size: 0.95rem;
  letter-spacing: 0;
}
.web-bar + .frame {
  margin-top: 3.25rem; /* the bar's height (--bar-height) */
}
.bar-start {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
/* the page's title in the brand green, bold, as a poster page's collection (PosterPage.vue) */
.page-title {
  color: var(--green);
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.35;
}
/* Back as on a poster's page in dark mode (PosterPage.vue): the arrow, brighter on hover */
.web-bar .back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 4px;
  color: #d0d0d0;
}
.web-bar .back:hover {
  color: white;
}
.web-bar .back svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: currentColor;
}
.web-bar .download {
  font: inherit;
  font-weight: 700;
  line-height: 1;
  padding: 0.5rem 1rem;
  border-radius: 1.2rem;
  border: none;
  box-shadow: none;
  cursor: pointer;
  color: var(--green);
  background: #222;
  text-decoration: none;
}
.web-bar .download:hover {
  background: #333;
}
.web-bar .back:focus-visible,
.web-bar .download:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
/* a crop of the logo's 16:9 video, the letters and their reflection: 5.5% off the left, 28%
   off the top */
.logo {
  --x: 2.83; --y: 3.75;
  --width: calc(26.43 * var(--u));
  height: calc(11.69 * var(--u));
  overflow: hidden;
}
.logo-frame {
  position: absolute;
  left: calc(-1.73 * var(--u));
  top: calc(-4.96 * var(--u));
  width: calc(31.46 * var(--u));
  height: calc(17.7 * var(--u));
}
.logo-frame > img {
  width: 100%;
  height: 100%;
}
.logo-frame :deep(figcaption) {
  display: none; /* the design has no room for the credit */
}
.kicker { --x: 30.3; --y: 3.5; --size: 1.399; font-weight: 300; color: var(--green); }
.title {
  --x: 30.3; --y: 5.8; --size: 3.578;
  line-height: 0.97;
  font-weight: 300;
  text-transform: uppercase;
}
.tagline { --x: 2.83; --y: 17.2; --size: 1.557; font-weight: 700; color: var(--green); }
.lead { --x: 2.83; --y: 20.5; --size: 1.399; --width: calc(53.8 * var(--u)); }
.audiences { --x: 2.9; --y: 28.9; --size: 1.35; font-weight: 700; white-space: nowrap; }
.audiences > span:not(.pair),
.audiences .pair span {
  color: var(--green);
}
.heading {
  font-weight: 700;
  color: var(--green);
  text-transform: uppercase;
}
/* Format's first sentence a little tighter, so the paragraph keeps the design's nine lines
   (Seán, 2026-11-05: "research, industry, and visual arts") */
.body .tight {
  letter-spacing: -0.01em;
}
.body p {
  margin: 0;
  line-height: inherit; /* not global.css's 1.5rem */
}
.heading.format { --x: 2.9; --y: 32.7; }
.body.format { --x: 2.9; --y: 35.2; --width: calc(26.4 * var(--u)); }
.heading.value { --x: 30.1; --y: 32.7; }
.body.value { --x: 30.1; --y: 35.2; --width: calc(26.5 * var(--u)); }
.heading.highlights { --x: 2.8; --y: 51.8; }
.heading.closing { --x: 3; --y: 70; }
.body.closing { --x: 3; --y: 72.5; --width: calc(26.4 * var(--u)); }
.heading.leadership { --x: 30.3; --y: 69.9; }
.body.leadership { --x: 30.6; --y: 72.5; --width: calc(26.5 * var(--u)); }
.contact {
  margin-top: 1.4em !important;
}
.contact span {
  margin: 0 0.8em;
}
.rule {
  --x: 2.9;
  --width: calc(53.7 * var(--u));
  height: 0;
  border: none;
  border-top: calc(0.1 * var(--u)) solid #fefefe;
}
.rule.top { --y: 54; }
.rule.bottom { --y: 67.7; }
.cover {
  --y: 54.83;
  --width: calc(8.36 * var(--u));
  display: block;
  height: calc(11.53 * var(--u));
}
.cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cover.methods { --x: 2.83; }
.cover.nature { --x: 13.03; }
.map {
  --x: 41.9; --y: 54.6;
  --width: calc(14.7 * var(--u));
  height: calc(8.1 * var(--u));
  object-fit: contain;
}
.figures {
  inset: 0;
  pointer-events: none;
}
/* a figure's link: the number in its own white, underlined only under the pointer */
.sheet .figure a {
  pointer-events: auto;
  color: inherit;
  text-decoration: none;
}
.sheet .figure a:hover,
.sheet .figure a:focus-visible {
  text-decoration: underline;
  text-decoration-thickness: 0.06em;
}
.figure {
  position: absolute;
  left: calc(var(--x) * var(--u));
  top: calc(var(--y) * var(--u));
  display: flex;
  flex-direction: column-reverse;
}
/* the numbers a touch larger than the design's 27.9pt, their line a little tighter, so the
   label sits closer under its number than beside the next column (Seán, 2026-11-05) */
.figure dd {
  margin: 0;
  font-size: calc(2.9 * var(--u));
  line-height: 1.3;
  font-weight: 800;
}
/* the labels a little smaller and tighter than the design's 12pt, so "Community members"
   fits on one line beside Posters (Seán, 2026-11-05) */
.figure dt {
  font-size: calc(1.1 * var(--u));
  line-height: 1.4;
  letter-spacing: -0.01em;
}
.figure.row1 { --y: 54.57; }
.figure.row2 { --y: 61.7; }
.figure.col1 { --x: 22.9; }
.figure.col2 { --x: 35.4; }
.figure.col3 { --x: 42.5; }
/* On the web, wider screens: the sheet as a page in a PDF viewer, on grey, with margins at the
   sides and foot (Seán, 2026-11-05; PAGE_MARGIN) */
.one-pager-page:not(.isPdf) {
  --desk: #2a2a2e;
  background: var(--desk);
}
.one-pager-page:not(.isPdf) .web-bar {
  background: var(--desk);
}
.one-pager-page:not(.isPdf) .web-bar + .frame {
  margin-top: calc(3.25rem + 12px); /* a little grey above the page, as below the bar */
}
.one-pager-page:not(.isPdf) .frame {
  margin-bottom: 24px; /* PAGE_MARGIN */
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.5);
}
/* Narrow screens (the site's narrow and medium widths, under 580px: useDisplayWidth.js): one
   column at the design's own sizes, not the sheet scaled down, in the sheet's order: the top,
   Format, Value, Highlights, the closing section, Leadership (Seán, 2026-11-05). The PDF is
   made at 794px, so it never stacks */
@media (max-width: 579px) {
  /* no page here: the text to the screen's edges, on black */
  .one-pager-page:not(.isPdf) {
    --desk: black;
  }
  .one-pager-page:not(.isPdf) .frame {
    margin-bottom: 0;
    box-shadow: none;
  }
  .one-pager-page:not(.isPdf) .web-bar + .frame {
    margin-top: 3.25rem;
  }
  /* every size below the bar is in --u: one step larger, the same for all text (Seán,
     2026-11-05: body text 18px, was 16px) */
  .one-pager-page {
    --u: calc(794px / 59.55 * 1.125);
  }
  .web-bar {
    width: 100%;
    padding: 0 1rem;
  }
  .frame {
    width: auto;
    height: auto;
    overflow: visible;
  }
  .sheet {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1rem;
    width: auto;
    height: auto;
    padding: 1rem 1rem 5rem;
    transform: none;
  }
  .sheet > * {
    position: static;
    left: auto;
    top: auto;
    grid-column: 1 / -1;
    width: auto;
  }
  .logo {
    aspect-ratio: 26.43 / 11.69;
    height: auto;
    position: relative;
  }
  .logo-frame {
    left: -6.55%;
    top: -42.4%;
    width: 119%;
    height: 151.4%;
  }
  .kicker {
    margin-top: 1rem;
  }
  /* three lines fitted to the column (Seán, 2026-11-05): each font size the column's width
     (--column, the sheet's padding off) over its longest line's width in ems, measured in Nunito
     Sans (VISUALIZING 6.11em; the tagline's first sentence 17.74em; the audiences' second pair
     20.6em), with a little room spare */
  .sheet {
    --column: calc(100vw - 2rem);
  }
  .title {
    font-size: calc(var(--column) / 6.11 * 0.98);
  }
  .tagline,
  .lead,
  .audiences {
    margin-top: 1rem;
  }
  /* the lead and the sections' text one size, between the design's two (14 and 12pt; Seán,
     2026-11-05) */
  .lead,
  .body {
    --size: 1.3;
  }
  /* one sentence a line, both at the size the longer one fits */
  .tagline {
    font-size: calc(var(--column) / 17.74 * 0.97);
  }
  .tagline span {
    display: block;
    white-space: nowrap;
  }
  /* two lines, a pair on each; bold, white as the text above */
  .audiences {
    font-size: calc(var(--column) / 20.6 * 0.97);
    white-space: normal;
  }
  .audiences .pair {
    white-space: nowrap;
  }
  .heading {
    margin-top: 1.75rem;
    margin-bottom: 0.5rem;
  }
  .heading.highlights {
    margin-bottom: 1rem; /* no rule under it here */
  }
  .rule {
    display: none; /* neither rule: the headings alone mark the sections (Seán, 2026-11-05) */
  }
  .cover {
    grid-column: auto;
    height: auto;
    aspect-ratio: 8.36 / 11.53;
  }
  .figures {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 1.25rem;
    pointer-events: auto;
  }
  .figure {
    position: static;
  }
  .figure.col3 {
    grid-column: 1 / -1; /* "Website visitors last year" on one line */
  }
  .map {
    height: auto;
    margin-top: 1.25rem;
  }
}
/* the version switch (admins and editors): the site's segmented control, light text on a dark
   pill, floating over the sheet's foot */
.version-switch {
  --text-color: #e8e8e8;
  position: fixed;
  left: 50%;
  bottom: 1.25rem;
  z-index: 20;
  transform: translateX(-50%);
  padding: 0.3rem;
  border-radius: 999px;
  background: rgba(34, 34, 34, 0.92);
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.6);
  letter-spacing: 0;
}
/* both options the same width, room for the longer label whole ("General", not "Gener…"): the
   control's equal flex columns (flex: 1 1 0) collapse to their minimum in this pill, and a
   flex-basis alone isn't what the pill sizes itself by (Safari: Ask overflowed); a width is */
.version-switch :deep(button) {
  flex: 0 0 auto;
  width: 6.2em;
}
</style>
